import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import { ToastProvider, RoleContext } from './components/shared';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Invoices from './pages/Invoices';
import Reports from './pages/Reports';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import { Engineers, EngineerProfile } from './pages/Engineers';
import { FieldVisits, FieldVisitPage } from './pages/FieldVisits';
import Opportunities from './pages/Opportunities';
import PartNeeds from './pages/PartNeeds';
import Calendar from './pages/Calendar';
import Maintenance from './pages/Maintenance';
import QrLabels from './pages/QrLabels';
import DeviceScan from './pages/DeviceScan';
import ContractDetail from './pages/ContractDetail';
import VisitPage from './pages/VisitPage';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Logo from './components/Logo';
import ClientPortal from './pages/ClientPortal';

function App() {
  const [role, setRole] = useState(localStorage.getItem('pf_auth') || null);
  const [clientId, setClientId] = useState(localStorage.getItem('pf_client_id') || null);
  const [clientName, setClientName] = useState(localStorage.getItem('pf_client_name') || null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem('pf_theme') === 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('pf_theme', dark ? 'dark' : 'light');
  }, [dark]);

  function handleLogin(r, cid, cname) {
    setRole(r);
    setClientId(cid || null);
    setClientName(cname || null);
  }

  function handleLogout() {
    const token = localStorage.getItem('pf_token');
    if (token) fetch('/api/users/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true }).catch(() => {});
    localStorage.removeItem('pf_auth');
    localStorage.removeItem('pf_client_id');
    localStorage.removeItem('pf_client_name');
    localStorage.removeItem('pf_username');
    localStorage.removeItem('pf_token');
    setRole(null); setClientId(null); setClientName(null);
  }

  if (!role) return <Login onLogin={handleLogin} />;

  // Client viewer — simple portal, no sidebar
  if (role === 'client_viewer') {
    return (
      <RoleContext.Provider value={role}>
        <ToastProvider>
          <BrowserRouter>
            <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
              <div style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Logo size={26} />
                <span className="text-dim text-sm">|</span>
                <span className="text-sm fw-500">{clientName}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="theme-toggle" onClick={() => setDark(v => !v)}>{dark ? '☀️' : '🌙'}</button>
                  <button className="btn btn-danger btn-sm" onClick={handleLogout}>Logout</button>
                </div>
              </div>
              <ClientPortal clientId={clientId} clientName={clientName} dark={dark} setDark={setDark} onLogout={handleLogout} />
            </div>
          </BrowserRouter>
        </ToastProvider>
      </RoleContext.Provider>
    );
  }

  return (
    <RoleContext.Provider value={role}>
      <ToastProvider>
        <BrowserRouter>
          <div className="layout">
            <Sidebar onLogout={handleLogout} role={role} open={sidebarOpen} onClose={() => setSidebarOpen(false)} dark={dark} setDark={setDark} />
            <div className="main">
              <div className="topbar">
                <button className="hamburger" onClick={() => setSidebarOpen(v => !v)}>☰</button>
                <span className="topbar-title"><Logo size={24} /></span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="theme-toggle" onClick={() => setDark(v => !v)}>{dark ? '☀️' : '🌙'}</button>
                  <button className="btn btn-danger btn-sm" onClick={handleLogout}>Logout</button>
                </div>
              </div>
              <Routes>
                <Route path="/" element={<Dashboard dark={dark} setDark={setDark} />} />
                <Route path="/devices" element={<Devices />} />
                <Route path="/devices/:id" element={<DeviceDetail />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/tickets/:id" element={<TicketDetail />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/field-visits" element={<FieldVisits />} />
                <Route path="/field-visits/:id" element={<FieldVisitPage />} />
                <Route path="/engineers" element={<Engineers />} />
                <Route path="/engineers/:id" element={<EngineerProfile />} />
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/part-needs" element={<PartNeeds />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/maintenance/labels" element={<QrLabels />} />
                <Route path="/maintenance/visit/:id" element={<VisitPage />} />
                <Route path="/maintenance/:id" element={<ContractDetail />} />
                <Route path="/d/:token" element={<DeviceScan />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </div>
          </div>
        </BrowserRouter>
      </ToastProvider>
    </RoleContext.Provider>
  );
}

export default App;
